// @ts-check
/** @typedef {import('../types/tagging.types').OrganisationTag} OrganisationTag */

import axios from 'axios';
import { useAuthenticatedQuery } from '../useAuthenticatedQuery';
import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '@home/config';
import { useOrgContext } from '../admin/organisation/AdminOrgIDContext';

/**
 * Hook to fetch organisation tags and their values.
 * Uses authentication context to get a token.
 * Returns an array of tags, each with its associated values.
 * Relies on the current organisation from context.
 * 
 * @returns {{
 *  orgTags: OrganisationTag[],
 *  isLoading: boolean,
 *  error: any,
 *  refetch: ()=> Promise<any>,
 * }}
 */
export default function useOrganisationTags() {
  const { organisationData } = useOrgContext();   // Get current org data

  const orgIdToUse = organisationData?.id;

  const fetchOrgTags = async (token) => {
    const resp = await axios.get(`${API_BASE_URL}organisation_tags`, {
      headers: { Authorization: `Bearer ${token}`, 'x-functions-key': FUNCTION_KEY },
      params: { org_id: orgIdToUse },
    });

    const parsedTags = resp.data.map(tag => ({
        ...tag,
        is_filter: !!tag.is_filter,
        is_groupable: !!tag.is_groupable,
        multi_value: !!tag.multi_value,
        tag_values: (tag.tag_values ?? []).sort((a, b) => a.order_by - b.order_by),
    })).sort((a, b) => a.tag_order_by - b.tag_order_by);

    return parsedTags
  };

  const { data = [], isLoading, error, refetch } = useAuthenticatedQuery(
    ['orgTags', orgIdToUse],
    fetchOrgTags,
    [PROJECT_SCOPE],
    {
      enabled: !!organisationData?.id, // the context defaults to {} before the organisation loads
      staleTime: 10 * 60 * 1000,   // tags/values rarely change
    }
  );

  return { orgTags: data, isLoading, error, refetch };
}
